import { ContentLayout, CustomeDialog, HeaderBreadcrumbs } from "../../../shared";
import { useState, useRef, useEffect } from "react";
import {
  Button,
  Stack,
  Card,
  Typography,
  InputAdornment,
  Switch,
  FormHelperText,
  TextField,
  Checkbox,
  createFilterOptions,
  Tooltip,
  Chip,
  Link,
} from "@mui/material";
import InputControl from "../../../shared/components/InputControl";
import { useNavigate, useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";

import { useLoyalty } from "../hooks";
import { AsyncPaginate } from "react-select-async-paginate";
import { useStore } from "../../Stores";
import useLoyaltyGroups from "../hooks/useLoyaltyGroups";
import AddIcon from "@mui/icons-material/Add";

export const LoyaltyAddorEditSponsorshipGroup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const updateMode = id ? id : false;
  const {
    appState: { globalData },
    state,
    onChange,
    handleGetCustomerList,
    deleteSelectedStore,
    onSaveGroup,
    onUpdateGroup,
    onOwnerChange,
    handleGetMemberList,
    handleModal,
  } = useLoyaltyGroups({ isGroupList: false, groupId: id });
  const { storeList } = useStore({ isStore: true, storeId: null });
  const {
    data: {
      club_name,
      customer_id,
      barcode,
      verification_code,
      member_number,
      store_id,
      status,
      starting_points,
      loyalty_card_type_id,
      multiplier,
    },
    name,
    isDetailsFetching,
    multiplierModal,
  } = state;
  const [, forceUpdate] = useState();
  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      validators: {
        twoDecimal: { 
          message: 'The value must be a number with up to two decimal places.',
          rule: (val) => /^-?\d+(\.\d{1,2})?$/.test(val),
          required: true,
        }
      },
    })
  );

  const onSubmit = (state) => {
    if (validator.current.allValid()) {
      if (multiplier == 0 && state) {
        handleModal(true)
      } else {
        id ? onUpdateGroup(id) : onSaveGroup();
      }
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      border: "1px solid #E6E8F0",
      height: "48px",
    }),
    option: (provided, state) => ({
      ...provided,
      maxHeight: "239px",
    }),
    menu: (provided, state) => ({
      ...provided,
      zIndex: 99,
    }),
  };

  const storeOptions = storeList.map((v) => {
    return { id: v.id, name: v.name };
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.name,
  });

  const CustomOption = (props) => {
    const { data, innerRef, innerProps } = props;

    return (
      <>
        {data.custom ? (
          <Stack
            ref={innerRef}
            {...innerProps}
            sx={{
              flexDirection: "row",
              p: "7px 8px",
              gap: "2px",
              alignItems: "center",
            }}
          >
            <a
              style={{ textDecoration: "none" }}
              target="_blank"
              href="/customers/create"
            >
              <Stack direction={"row"}>
                <AddIcon
                  sx={{
                    color: "#1560D4",
                  }}
                />
                <Link sx={{ textDecoration: "none" }}>Add new Customer</Link>
              </Stack>
            </a>
          </Stack>
        ) : (
          <Stack
            ref={innerRef}
            {...innerProps}
            sx={{
              p: "7px 10px",
              gap: "2px",
            }}
          >
            <Typography variant="subtitle2" fontWeight={400} lineHeight="17px">
              {data.label}
            </Typography>
            {!id && (
              <Typography color="#666666" variant="caption" lineHeight="15px">
                {data.email}, {data.phone}
              </Typography>
            )}
          </Stack>
        )}
      </>
    );
  };
  return (
    <>
      {validator.current.purgeFields()}
      <ContentLayout
        title={id ? name : "New Card"}
        rightContent={
          <>
            <Button
              type="button"
              size="medium"
              variant="contained"
              onClick={() => onSubmit(true)}
            >
              {id ? "Update" : "Save"}
            </Button>
          </>
        }
        breadcrumbs={
          <>
            <HeaderBreadcrumbs
              links={[
                { name: "Dashboard", href: "/" },
                { name: "Loyalty Cards", href: "/loyalty-cards" },
                {
                  name: "Groups",
                  href: `/loyalty-cards?groups`,
                },
                id
                  ? {
                    name: name,
                    href: `/loyalty-cards/sponsorship-group/${id}`,
                  }
                  : false,
                { name: id ? "Edit" : "Create" },
              ].filter(Boolean)}
            />
          </>
        }
      >
        <Card sx={{ p: "32px 24px" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 3, sm: 15 }}
            gap="24px"
          >
            <Typography variant="subtitle1" fontWeight={600} minWidth="145px">
              Basic Details
            </Typography>

            <Stack width={"100%"} gap="20px">
              <InputControl
                name="club_name"
                label="Group Name"
                required
                onChange={onChange}
                value={club_name}
                error={validator.current.message(
                  "Club Name",
                  club_name,
                  "required"
                )}
                helperText={validator.current.message(
                  "Club Name",
                  club_name,
                  "required"
                )}
              />
              <Stack>
                {id ? (
                  <>
                    <AsyncPaginate
                      debounceTimeout={1000}
                      value={
                        Object.keys(customer_id).length ? customer_id : false
                      }
                      placeholder="Owner Name*"
                      className="AsyncPaginate-dropdown"
                      classNamePrefix="react-select"
                      components={{ Option: CustomOption }}
                      loadOptions={(e, p, { page }) => {
                        return handleGetMemberList({
                          id: id,
                          page,
                        });
                      }}
                      onChange={(e) => {
                        onOwnerChange({
                          target: {
                            name: "customer_id",
                            value: e,
                          },
                        });
                      }}
                      additional={{
                        page: 1,
                      }}
                      styles={customStyles}
                    />
                    <FormHelperText sx={{ color: "#C60808" }}>
                      {validator.current.message(
                        "Customer Name",
                        customer_id.value,
                        "required"
                      )}
                    </FormHelperText>
                  </>
                ) : (
                  <>
                    <AsyncPaginate
                      debounceTimeout={1000}
                      value={
                        Object.keys(customer_id).length ? customer_id : false
                      }
                      placeholder="Owner Name*"
                      className="AsyncPaginate-dropdown"
                      classNamePrefix="react-select"
                      components={{ Option: CustomOption }}
                      loadOptions={(e, p, { page }) => {
                        return handleGetCustomerList({
                          search: e,
                          page,
                        });
                      }}
                      onChange={(e) => {
                        onOwnerChange({
                          target: {
                            name: "customer_id",
                            value: e,
                          },
                        });
                      }}
                      additional={{
                        page: 1,
                      }}
                      styles={customStyles}
                    />
                    <FormHelperText sx={{ color: "#C60808" }}>
                      {validator.current.message(
                        "Customer Name",
                        customer_id.value,
                        "required"
                      )}
                    </FormHelperText>
                  </>
                )}
              </Stack>

              <InputControl
                name="barcode"
                label="Owner Barcode"
                type="outlinedInput"
                inputType="text"
                inputLabel="Owner Barcode"
                placeholder="Owner Barcode"
                size="small"
                onChange={onChange}
                disabled={id}
                value={barcode}
                endAdornment={
                  <InputAdornment position="start">
                    <Tooltip title="If left empty,it will be auto generated">
                      <img alt="details" src="/icons/ic_info.svg" />
                    </Tooltip>
                  </InputAdornment>
                }
              />
              <InputControl
                type="outlinedInput"
                inputType="text"
                inputLabel="Member Number"
                label="Member Number"
                placeholder="Member Number"
                key={id}
                disabled={id}
                name="member_number"
                size="small"
                onChange={onChange}
                value={member_number}
                endAdornment={
                  <InputAdornment position="start">
                    <Tooltip title="If left empty,it will be auto generated">
                      <img alt="details" src="/icons/ic_info.svg" />
                    </Tooltip>
                  </InputAdornment>
                }
              />

              <Stack width={"100%"} gap="20px">
                <InputControl
                  type="outlinedInput"
                  inputType="text"
                  inputLabel="Verification Code"
                  label="Verification Code"
                  placeholder="Verification Code"
                  name="verification_code"
                  size="small"
                  key={`${isDetailsFetching}5`}
                  value={verification_code}
                  onChange={onChange}
                  disabled={id}
                  error={validator.current.message(
                    "Verification Code",
                    verification_code,
                    "alpha_num|size:6"
                  )}
                  helperText={validator.current.message(
                    "Verification Code",
                    verification_code,
                    "alpha_num|size:6"
                  )}
                  endAdornment={
                    <InputAdornment position="start">
                      <Tooltip title="If left empty, will be auto-generated.">
                        <img alt="details" src="/icons/ic_info.svg" />
                      </Tooltip>
                    </InputAdornment>
                  }
                />

                <Stack>
                  <>
                    <InputControl
                      type="dropdown"
                      name="store_id"
                      label="Store"
                      multiple={true}
                      options={storeOptions ?? []}
                      value={store_id}
                      disableCloseOnSelect
                      disableClearable={true}
                      onChange={(e, v) =>
                        onChange({
                          target: {
                            name: "store_id",
                            value: v,
                          },
                        })
                      }
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.name === value.name
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => <></>)
                      }
                      renderOption={(props, option, { selected }) => (
                        <>
                          <li {...props} key={option.value}>
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.name}
                          </li>
                        </>
                      )}
                      filterOptions={filterOptions}
                      sx={{ width: "100%" }}
                      componentsProps={{
                        paper: {
                          sx: {
                            width: "100%",
                            minWidth: 250,
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Store"
                          placeholder="Store"
                        />
                      )}
                    />

                    {store_id.length === 0 && (
                      <Stack direction="row" marginTop={2}>
                        <img alt="edit" src="/icons/icon_not.svg" />
                        <Typography
                          color="#636363"
                          fontWeight={400}
                          fontSize="14px"
                          ml={1}
                        >
                          If not selected all stores are applicable.
                        </Typography>
                      </Stack>
                    )}
                  </>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginTop: 2,
                    }}
                  >
                    {store_id.map((data, index) => (
                      <Chip
                        key={index}
                        label={
                          <Typography variant="body2" fontSize="14px">
                            {data.name}
                          </Typography>
                        }
                        onDelete={() => {
                          deleteSelectedStore(data.id);
                        }}
                        sx={{
                          backgroundColor: "#F2F2F2",
                          height: "30px",
                          "& .MuiChip-deleteIcon": {
                            color: "#CCCCCC",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="space-between" py="10px">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Status
                  </Typography>
                  <Switch
                    name="status"
                    checked={status}
                    onChange={(e) =>
                      onChange({
                        target: {
                          value: e.target.checked ? 1 : 0,
                          name: "status",
                        },
                      })
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{ p: "32px 24px", mt: "24px" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 3, sm: 15 }}
            gap="24px"
          >
            <Typography variant="subtitle1" fontWeight={600} minWidth="96px">
              Point Configuration
            </Typography>

            <Stack width={"100%"} gap="20px">
              <InputControl
                type="outlinedInput"
                inputType="number"
                inputLabel="Starting Points*"
                label="Starting Points*"
                placeholder="Starting Points*"
                name="starting_points"
                size="small"
                key={`${isDetailsFetching}3`}
                disabled={id}
                onChange={onChange}
                value={starting_points}
                error={validator.current.message(
                  "Starting Points",
                  starting_points,
                  "required"
                )}
                helperText={validator.current.message(
                  "Starting Points",
                  starting_points,
                  "required"
                )}
                endAdornment={
                  <InputAdornment position="start">
                    <Tooltip title="Points allotted to the group upon its creation">
                      <img alt="details" src="/icons/ic_info.svg" />
                    </Tooltip>
                  </InputAdornment>
                }
              />
              <InputControl
                type="select"
                name="loyalty_card_type_id"
                label="Card Type*"
                key={`${isDetailsFetching}2`}
                disabled={id}
                optionValue={
                  globalData.loyalty_cards?.loyalty_card_types?.filter(
                    (item) => item.value !== 1
                  ) || []
                }
                value={loyalty_card_type_id}
                onChange={onChange}
                error={validator.current.message(
                  "Card Type",
                  loyalty_card_type_id,
                  "required"
                )}
                helperText={validator.current.message(
                  "Card Type",
                  loyalty_card_type_id,
                  "required"
                )}
              />
              <InputControl
                type="outlinedInput"
                inputType="number"
                inputLabel="Multiplier *"
                label="Multiplier *"
                placeholder="Multiplier *"
                key={`${isDetailsFetching}1`}
                name="multiplier"
                size="small"
                onChange={onChange}
                value={multiplier}
                error={validator.current.message(
                  "Multiplier",
                  multiplier,
                  "required|twoDecimal"
                )}
                helperText={validator.current.message(
                  "Multiplier",
                  multiplier,
                  "required|twoDecimal"
                )}
                endAdornment={
                  <InputAdornment position="start">
                    <Tooltip title="Point % relative to the purchase-point% of the group's card type">
                      <img alt="details" src="/icons/ic_info.svg" />
                    </Tooltip>
                  </InputAdornment>
                }
              />
            </Stack>
          </Stack>
        </Card>
      </ContentLayout>
      <CustomeDialog
        open={multiplierModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        size="sm"
        isHeader={false}
        PaperProps={{
          sx: {
            width: "423px",
            height: "160px",
            p: "24px",
          },
        }}
        content={
          <Stack>
            <Typography fontWeight={500}>Setting the multiplier to '0' will prevent this group from earning any points. Are you sure you want to proceed?</Typography>
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: "10px"
              }}
            >
              <Button
                onClick={() => handleModal(false)}
              >
                No
              </Button>
              <Button
                onClick={() => onSubmit(false)}
                variant="contained"
              >
                Yes
              </Button>
            </Stack>
          </Stack>
        }
      />
    </>
  );
};
